import React, { useState, useRef } from 'react';
import { Upload, FileText, CheckCircle, AlertCircle, Download } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';

export const Admin: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        setFile(selectedFile);
        setError(null);
      } else {
        setError('Alleen .docx bestanden zijn toegestaan');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/master/import', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Upload failed');
      }

      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadExample = () => {
    // Create a simple example document structure
    const exampleContent = `# AI Geletterdheid Training

## Wat is AI?

### Inhoud
Kunstmatige Intelligentie (AI) is een tak van de computerwetenschap die zich richt op het creëren van systemen die taken kunnen uitvoeren die normaal gesproken menselijke intelligentie vereisen.

### Flashcards
Machine Learning — Een subset van AI waarbij computers leren van data zonder expliciet geprogrammeerd te worden
Deep Learning — Een subset van machine learning gebaseerd op kunstmatige neurale netwerken
Neuraal Netwerk — Een AI model geïnspireerd door het menselijk brein

### Quiz
Vraag: Wat is de definitie van Kunstmatige Intelligentie?
A: Computers die kunnen denken zoals mensen
B: Het vermogen van machines om taken uit te voeren die normaal menselijke intelligentie vereisen
C: Alleen robots die kunnen lopen
D: Software die altijd correcte antwoorden geeft
Antwoord: B

Vraag: Wat is Machine Learning?
A: Een subset van AI waarbij computers leren van data
B: Een subset van AI waarbij computers leren van data zonder expliciet geprogrammeerd te worden
C: Hetzelfde als AI
D: Een programmeertaal
Antwoord: B

## Geschiedenis van AI

### Inhoud
De geschiedenis van AI begint in de jaren 1950 met pioniers zoals Alan Turing en John McCarthy.

### Flashcards
Turing Test — Een test om te bepalen of een machine intelligent genoeg is om als mens door te gaan
Dartmouth Conference — De conferentie in 1956 waar de term "Artificial Intelligence" werd bedacht

### Quiz
Vraag: Wanneer werd de term "Artificial Intelligence" bedacht?
A: 1950
B: 1956
C: 1960
D: 1970
Antwoord: B

Vraag: Wie publiceerde de Turing Test?
A: John McCarthy
B: Alan Turing
C: Marvin Minsky
D: Herbert Simon
Antwoord: B`;

    const blob = new Blob([exampleContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'example-ai-training.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Admin Dashboard
        </h1>
        <p className="text-lg text-gray-600">
          Beheer content en importeer nieuwe trainingen.
        </p>
      </div>

      {/* Import Section */}
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Content Import
          </h2>
          <p className="text-gray-600">
            Upload een .docx bestand om nieuwe training content te importeren.
          </p>
        </div>

        <div className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selecteer .docx bestand
            </label>
            <div className="flex items-center space-x-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center space-x-2"
              >
                <Upload className="w-4 h-4" />
                <span>Bestand Selecteren</span>
              </Button>
              {file && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span>{file.name}</span>
                  <span className="text-gray-400">
                    ({(file.size / 1024 / 1024).toFixed(2)} MB)
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          {file && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="w-full sm:w-auto"
            >
              {uploading ? 'Uploaden...' : 'Upload en Importeer'}
            </Button>
          )}

          {/* Error Display */}
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Success Display */}
          {result && (
            <div className="space-y-4">
              <div className="flex items-center space-x-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
                <span className="text-green-700">Import succesvol!</span>
              </div>
              
              {result.data && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Import Resultaten:</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <div className="text-gray-500">Thema's</div>
                      <div className="font-semibold">{result.data.themes?.length || 0}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Onderwerpen</div>
                      <div className="font-semibold">{result.data.topics?.length || 0}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Secties</div>
                      <div className="font-semibold">{result.data.sections?.length || 0}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Flashcards</div>
                      <div className="font-semibold">{result.data.flashcards?.length || 0}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Vragen</div>
                      <div className="font-semibold">{result.data.questions?.length || 0}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Example Document */}
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Document Structuur
          </h2>
          <p className="text-gray-600">
            Download een voorbeeld om te zien hoe je document gestructureerd moet zijn.
          </p>
        </div>

        <div className="space-y-4">
          <Button
            variant="outline"
            onClick={downloadExample}
            className="flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>Download Voorbeeld</span>
          </Button>

          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-2">Document Structuur:</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• <strong>H1:</strong> Titel van de training</li>
              <li>• <strong>H2:</strong> Onderwerp titels</li>
              <li>• <strong>H3 "Inhoud":</strong> Content secties</li>
              <li>• <strong>H3 "Flashcards":</strong> Term — Uitleg format</li>
              <li>• <strong>H3 "Quiz":</strong> Vraag, A/B/C/D keuzes, Antwoord</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* System Status */}
      <Card>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">
            Systeem Status
          </h2>
          <p className="text-gray-600">
            Controleer de status van het systeem en recente imports.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <div className="font-semibold text-green-900">Database</div>
            <div className="text-sm text-green-700">Online</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <FileText className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <div className="font-semibold text-blue-900">Import Service</div>
            <div className="text-sm text-blue-700">Actief</div>
          </div>
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <AlertCircle className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <div className="font-semibold text-yellow-900">Cron Jobs</div>
            <div className="text-sm text-yellow-700">Actief</div>
          </div>
        </div>
      </Card>
    </div>
  );
};
